import type React from "react"
import { useCallback, useEffect, useRef } from "react"
import { useFormContext, useWatch } from "react-hook-form"

import type { AmountPercentagePair, CostBreakdownAmountPercentageValues } from "../types"
import { AmountPercentageCalculationType, CostBreakdownStandardField } from "../types"
import {
  calculateAmountPercentagePairUpdate,
  getCostBreakdownProductFieldPaths,
  recalculatePercentageForTotalBareCostChange,
  updateLastValues,
} from "../utils"

import { runWithGuardRef } from "@/utils/forms"

interface UseAmountPercentageCalculationsProps {
  productIndex: number
  totalBareCost: number | null
  isRecalculatingFromBareCost: React.MutableRefObject<boolean>
}

interface UseAmountPercentageCalculationsReturn {
  duties: {
    amount: number | null
    percentage: number | null
  }
  mpf: {
    amount: number | null
    percentage: number | null
  }
  hmf: {
    amount: number | null
    percentage: number | null
  }
}

export const useAmountPercentageCalculations = ({
  productIndex,
  totalBareCost,
  isRecalculatingFromBareCost,
}: UseAmountPercentageCalculationsProps): UseAmountPercentageCalculationsReturn => {
  const { setValue, control } = useFormContext()
  const fieldPaths = getCostBreakdownProductFieldPaths(productIndex)

  // Track last values for each field type to detect changes
  const lastValuesRefs = useRef<{
    duties: CostBreakdownAmountPercentageValues
    mpf: CostBreakdownAmountPercentageValues
    hmf: CostBreakdownAmountPercentageValues
    tariffs: Map<number, CostBreakdownAmountPercentageValues>
  }>({
    duties: { amount: null, percentage: null },
    mpf: { amount: null, percentage: null },
    hmf: { amount: null, percentage: null },
    tariffs: new Map(),
  })

  const duties = {
    amount: useWatch({ control, name: fieldPaths.duties.amount }),
    percentage: useWatch({ control, name: fieldPaths.duties.percentage }),
  }
  const mpf = {
    amount: useWatch({ control, name: fieldPaths.mpf.amount }),
    percentage: useWatch({ control, name: fieldPaths.mpf.percentage }),
  }
  const hmf = {
    amount: useWatch({ control, name: fieldPaths.hmf.amount }),
    percentage: useWatch({ control, name: fieldPaths.hmf.percentage }),
  }
  const tariffAmounts = useWatch({ control, name: fieldPaths.tariffs })

  const lastTotalBareCost = useRef<number | null>(null)

  // Helper to handle standard amount-percentage pairs
  const handleStandardAmountPercentagePair = useCallback(
    (fieldName: CostBreakdownStandardField, currentAmount: number | null, currentPercentage: number | null) => {
      if (isRecalculatingFromBareCost.current) return

      const lastValues = lastValuesRefs.current[fieldName]
      const update = calculateAmountPercentagePairUpdate(
        currentAmount,
        currentPercentage,
        lastValues.amount,
        lastValues.percentage,
        totalBareCost
      )

      if (update.type === AmountPercentageCalculationType.NoChange) return

      lastValuesRefs.current[fieldName] = updateLastValues(update.newAmount ?? null, update.newPercentage ?? null)

      if (update.type === AmountPercentageCalculationType.AmountChanged) {
        setValue(fieldPaths[fieldName].percentage, update.newPercentage ?? null)
      } else if (update.type === AmountPercentageCalculationType.PercentageChanged) {
        setValue(fieldPaths[fieldName].amount, update.newAmount)
      }
    },

    [setValue, fieldPaths, totalBareCost, isRecalculatingFromBareCost]
  )

  // Auto-calculation for duties
  useEffect(() => {
    handleStandardAmountPercentagePair(CostBreakdownStandardField.Duties, duties.amount, duties.percentage)
  }, [duties.amount, duties.percentage, handleStandardAmountPercentagePair])

  // Auto-calculation for mpf
  useEffect(() => {
    handleStandardAmountPercentagePair(CostBreakdownStandardField.Mpf, mpf.amount, mpf.percentage)
  }, [mpf.amount, mpf.percentage, handleStandardAmountPercentagePair])

  // Auto-calculation for hmf
  useEffect(() => {
    handleStandardAmountPercentagePair(CostBreakdownStandardField.Hmf, hmf.amount, hmf.percentage)
  }, [hmf.amount, hmf.percentage, handleStandardAmountPercentagePair])

  // Auto-calculation for tariffs with support for percentage-first entry
  useEffect(() => {
    if (!tariffAmounts || isRecalculatingFromBareCost.current) return

    tariffAmounts.forEach((tariff: AmountPercentagePair, tariffIndex: number) => {
      const currentTariffAmount = tariff?.amount ?? null
      const currentTariffPercentage = tariff?.percentage ?? null

      const lastCalculated = lastValuesRefs.current.tariffs.get(tariffIndex) || { amount: null, percentage: null }
      const update = calculateAmountPercentagePairUpdate(
        currentTariffAmount,
        currentTariffPercentage,
        lastCalculated.amount,
        lastCalculated.percentage,
        totalBareCost
      )

      if (update.type === AmountPercentageCalculationType.NoChange) return

      lastValuesRefs.current.tariffs.set(
        tariffIndex,
        updateLastValues(update.newAmount ?? null, update.newPercentage ?? null)
      )

      if (update.type === AmountPercentageCalculationType.AmountChanged) {
        setValue(`${fieldPaths.tariffs}.${tariffIndex}.percentage`, update.newPercentage ?? null)
      } else if (update.type === AmountPercentageCalculationType.PercentageChanged) {
        setValue(`${fieldPaths.tariffs}.${tariffIndex}.amount`, update.newAmount)
      }
    })
  }, [tariffAmounts, totalBareCost, setValue, fieldPaths, isRecalculatingFromBareCost])

  // Recalculate percentages when totalBareCost changes (from bareCostPerUom updates)
  useEffect(
    () => {
      if (!totalBareCost || totalBareCost === lastTotalBareCost.current) return

      runWithGuardRef(() => {
        // Recalculate standard fields
        const standardFields: CostBreakdownStandardField[] = [
          CostBreakdownStandardField.Duties,
          CostBreakdownStandardField.Mpf,
          CostBreakdownStandardField.Hmf,
        ]
        const standardValues = [duties, mpf, hmf]

        standardFields.forEach((fieldName, index) => {
          const currentAmount = standardValues[index].amount
          if (currentAmount && currentAmount > 0) {
            const calculatedPercentage = recalculatePercentageForTotalBareCostChange(currentAmount, totalBareCost)
            lastValuesRefs.current[fieldName] = updateLastValues(currentAmount, calculatedPercentage)
            setValue(fieldPaths[fieldName].percentage, calculatedPercentage)
          }
        })

        // Recalculate tariff percentages
        if (tariffAmounts) {
          tariffAmounts.forEach((tariff: AmountPercentagePair, tariffIndex: number) => {
            const currentTariffAmount = tariff?.amount ?? null
            if (currentTariffAmount && currentTariffAmount > 0) {
              const calculatedPercentage = recalculatePercentageForTotalBareCostChange(
                currentTariffAmount,
                totalBareCost
              )
              lastValuesRefs.current.tariffs.set(
                tariffIndex,
                updateLastValues(currentTariffAmount, calculatedPercentage)
              )
              setValue(`${fieldPaths.tariffs}.${tariffIndex}.percentage`, calculatedPercentage)
            }
          })
        }

        lastTotalBareCost.current = totalBareCost
      }, isRecalculatingFromBareCost)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [totalBareCost, duties, mpf, hmf, tariffAmounts, setValue, fieldPaths]
  )

  return {
    duties,
    mpf,
    hmf,
  }
}
